import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Chart as ChartJS } from 'chart.js';
import { fetchJSON } from '../api/apiClient';
import { useAuth } from './AuthContext';

const palettes = {
  orange: { primary: '#f97316', strong: '#ea580c', hover: '#fb923c', accent: '#c2410c', rgb: '249, 115, 22' },
  blue: { primary: '#3b82f6', strong: '#2563eb', hover: '#60a5fa', accent: '#1d4ed8', rgb: '59, 130, 246' },
  green: { primary: '#10b981', strong: '#059669', hover: '#34d399', accent: '#047857', rgb: '16, 185, 129' },
  pink: { primary: '#ec4899', strong: '#f43f5e', hover: '#f472b6', accent: '#ec4899', focus: '#be185d', rgb: '236, 72, 153' },
  berry: { primary: '#a21caf', strong: '#86198f', hover: '#c026d3', accent: '#9333ea', rgb: '162, 28, 175' },
};

export const themeOptions = [
  { key: 'orange', label: 'Original Orange', color: palettes.orange.primary },
  { key: 'blue', label: 'Electric Blue', color: palettes.blue.primary },
  { key: 'green', label: 'Emerald Green', color: palettes.green.primary },
  { key: 'pink', label: 'Vibrant Pink', color: palettes.pink.primary },
  { key: 'berry', label: 'Berry Plum', color: palettes.berry.primary },
];

const ThemeContext = createContext(null);
const normalizeTheme = value => value === 'slate' ? 'pink' : value;

export function ThemeProvider({ children }) {
  const { token, user, setUser } = useAuth();
  const [theme, setThemeState] = useState('orange');

  useEffect(() => {
    if (!token) {
      try {
        const legacyTheme = localStorage.getItem('nt_theme');
        const saved = normalizeTheme(legacyTheme);
        if (legacyTheme === 'slate') localStorage.setItem('nt_theme', saved);
        setThemeState(palettes[saved] ? saved : 'orange');
      } catch {
        setThemeState('orange');
      }
      return;
    }

    const savedTheme = normalizeTheme(user?.preferences?.theme);
    if (palettes[savedTheme]) {
      setThemeState(savedTheme);
      return;
    }

    let active = true;
    fetchJSON('/api/me')
      .then(profile => {
        if (!active) return;
        setUser(profile);
        const profileTheme = normalizeTheme(profile?.preferences?.theme);
        setThemeState(palettes[profileTheme] ? profileTheme : 'orange');
      })
      .catch(() => {
        if (active) setThemeState('orange');
      });
    return () => { active = false; };
  }, [token, user?.preferences?.theme, setUser]);

  const setTheme = useCallback((nextTheme) => {
    if (!palettes[nextTheme]) return;
    setThemeState(nextTheme);

    if (!token) {
      try {
        localStorage.setItem('nt_theme', nextTheme);
      } catch {
        // The selected theme still applies for this page.
      }
      return;
    }

    fetchJSON('/api/me/preferences', {
      method: 'PUT',
      body: { theme: nextTheme },
    }).then(setUser).catch(() => {
      setThemeState(palettes[user?.preferences?.theme] ? user.preferences.theme : 'orange');
    });
  }, [token, user?.preferences?.theme, setUser]);

  useEffect(() => {
    const palette = palettes[theme];
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.style.setProperty('--color-primary', palette.primary);
    root.style.setProperty('--color-primary-strong', palette.strong);
    root.style.setProperty('--color-primary-hover', palette.hover);
    root.style.setProperty('--color-accent', palette.accent);
    root.style.setProperty('--color-primary-rgb', palette.rgb);
    root.style.setProperty('--color-focus-ring', palette.focus || `rgba(${palette.rgb}, 0.2)`);
    root.style.colorScheme = 'light';

    ChartJS.defaults.color = '#0f172a';
    ChartJS.defaults.borderColor = '#cbd5e1';
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, setTheme, colors: palettes[theme] }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
